import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Bell, CalendarClock, PoundSterling, CheckCircle2, UserPlus } from "lucide-react";

const iconFor = (type) => {
  if (type === "deposit_paid") return PoundSterling;
  if (type === "booking_request") return CalendarClock;
  if (type === "new_lead") return UserPlus;
  return CheckCircle2;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data.items);
      setUnread(res.data.unread);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/read-all");
      setUnread(0);
    }
  };

  const openItem = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle} className="relative h-10 w-10 rounded-sm bg-secondary hover:bg-[#2a2a2e] flex items-center justify-center transition-[background-color] duration-200">
        <Bell className="h-5 w-5 text-foreground" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-[#18181b] border border-white/10 rounded-sm shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="font-display text-lg tracking-wide">NOTIFICATIONS</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No notifications yet.</p>
            ) : items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left flex gap-3 px-4 py-3 border-b border-white/5 hover:bg-secondary transition-[background-color] duration-150 ${!n.read ? "bg-primary/5" : ""}`}>
                  <div className="h-8 w-8 shrink-0 rounded-sm bg-primary/15 flex items-center justify-center"><Icon className="h-4 w-4 text-primary" /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
